import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { Panel, Group, Separator } from "react-resizable-panels";
import { TopologyCanvas } from "../components/canvas/TopologyCanvas.js";
import { TopologyDesignCanvas } from "../components/canvas/TopologyDesignCanvas.js";
import { CanvasToolbar } from "../components/canvas/CanvasToolbar.js";
import { AgentPalette } from "../components/canvas/AgentPalette.js";
import { PixelWorldCanvas, PixelWorldToolbar, DemoModePanel, PixelFullscreen } from "../pixel-world/index.js";
import type { TopologyInfo } from "../pixel-world/index.js";
import { FeedbackPanel } from "../components/panels/FeedbackPanel.js";
import { RunPanel } from "../components/panels/RunPanel.js";
import { AgentConfigPanel } from "../components/panels/AgentConfigPanel.js";
import { SharedContextPanel } from "../components/panels/SharedContextPanel.js";
import { InputRequestPanel } from "../components/panels/InputRequestPanel.js";
import { RunDetailPanel } from "../components/run/RunDetailPanel.js";
import { TopologySidebar } from "../components/topology/TopologySidebar.js";
import { TemplateGallery } from "../components/topology/TemplateGallery.js";
import { useWorldStore } from "../stores/world.js";
import { useScreenLayout } from "../hooks/useScreenLayout.js";

export function TopologyView() {
  const { topologyId } = useParams();
  const selectTopology = useWorldStore((s) => s.selectTopology);
  const selectedTopologyId = useWorldStore((s) => s.selectedTopologyId);
  const selectedRunId = useWorldStore((s) => s.selectedRunId);
  const selectRun = useWorldStore((s) => s.selectRun);
  const viewMode = useWorldStore((s) => s.viewMode);
  const mode = useWorldStore((s) => s.mode);
  const fullscreen = useWorldStore((s) => s.fullscreen);
  const templateGalleryOpen = useWorldStore((s) => s.templateGalleryOpen);
  const setTemplateGalleryOpen = useWorldStore((s) => s.setTemplateGalleryOpen);
  const { isUltraWide } = useScreenLayout();
  const [demoTopology, setDemoTopology] = useState<TopologyInfo | null>(null);
  const handleDemoTopologyChange = useCallback((topo: TopologyInfo) => setDemoTopology(topo), []);
  const isDemo = viewMode === "pixel" && !selectedTopologyId;

  // The URL is the single source of truth for which topology is selected:
  // a param selects it, no param clears it. (Avoids races with manual selection.)
  useEffect(() => {
    selectTopology(topologyId ?? null);
  }, [topologyId, selectTopology]);

  // Leaving the pixel view should also leave full-screen.
  useEffect(() => {
    if (viewMode !== "pixel" && fullscreen) {
      useWorldStore.getState().setFullscreen(false);
    }
  }, [viewMode, fullscreen]);

  return (
    <div className="h-full relative">
    <Group orientation="horizontal" id="topology-main">
      {/* Topology sidebar */}
      <Panel
        id="topo-sidebar"
        defaultSize="14%"
        minSize="10%"
        maxSize="25%"
        collapsible
        collapsedSize="0px"
      >
        <TopologySidebar />
      </Panel>
      <Separator className="w-px bg-border-subtle/20 resize-handle" />

      {/* Center: canvas + feedback */}
      <Panel id="topo-center" defaultSize="62%" minSize="40%">
        <Group orientation="vertical" id="canvas-feedback">
          <Panel id="canvas-panel" defaultSize="72%" minSize="40%">
            <div className="h-full w-full relative">
              {/* Show the view-mode toolbar where the canvas itself doesn't (the
                  monitor/design canvases render their own). */}
              {(viewMode === "pixel" || (!selectedTopologyId && mode !== "design")) && <CanvasToolbar />}
              {viewMode === "pixel" ? (
                <>
                  {!fullscreen && (
                    <PixelWorldCanvas topologyOverride={isDemo ? (demoTopology ?? undefined) : undefined} />
                  )}
                  <PixelWorldToolbar />
                  {isDemo && !fullscreen && <DemoModePanel onTopologyChange={handleDemoTopologyChange} />}
                </>
              ) : mode === "design" ? (
                <>
                  <TopologyDesignCanvas />
                  <AgentPalette />
                </>
              ) : (
                <TopologyCanvas />
              )}
            </div>
          </Panel>
          <Separator className="h-px bg-border-subtle/20 resize-handle" />
          <Panel
            id="feedback-panel"
            defaultSize="28%"
            minSize="10%"
            maxSize="60%"
            collapsible
            collapsedSize="0px"
          >
            <FeedbackPanel />
          </Panel>
        </Group>
      </Panel>
      <Separator className="w-px bg-border-subtle/20 resize-handle" />

      {/* Right detail panel */}
      <Panel
        id="detail-panel"
        defaultSize={isUltraWide ? "20%" : "24%"}
        minSize="14%"
        maxSize="40%"
        collapsible
        collapsedSize="0px"
      >
        <div className="h-full overflow-y-auto bg-surface-light/60 backdrop-blur-sm">
          <InputRequestPanel />
          <AgentConfigPanel />
          <RunPanel />
          {selectedTopologyId && (
            <SharedContextPanel topologyId={selectedTopologyId} />
          )}
        </div>
      </Panel>
    </Group>

    {/* Run detail slide-in — rendered outside resizable panels so it's not clipped */}
    <AnimatePresence>
      {selectedRunId && (
        <RunDetailPanel
          runId={selectedRunId}
          onClose={() => selectRun(null)}
        />
      )}
    </AnimatePresence>

    {/* Full-screen pixel-world presentation mode — fixed overlay covering the viewport */}
    <AnimatePresence>
      {fullscreen && viewMode === "pixel" && (
        <PixelFullscreen
          topologyOverride={isDemo ? (demoTopology ?? undefined) : undefined}
        />
      )}
    </AnimatePresence>

    {/* Topology template gallery — create from a pattern or start a blank build */}
    <TemplateGallery
      open={templateGalleryOpen}
      onClose={() => setTemplateGalleryOpen(false)}
    />
    </div>
  );
}
